// aiBackgroundJob — shared background runner for AI generation surfaces.
//
// Why: when the user taps "Analyse" on any AI card and then leaves the
// screen, the in-flight Gemini call must keep running so the result is
// ready (and cached) the moment they come back. The owning React
// component will unmount; its state is gone. So we hold job state at
// module scope, expose subscribe / isInFlight / lastError to mounted
// listeners, and fire a haptic on completion regardless of whether
// anyone is currently subscribed.
//
// Each job is keyed by a stable string (typically the localStorage cache
// key, e.g. `hayahReportInsight_macros_30`). Two calls to runJob with
// the same key while one is in flight are coalesced — the second is a
// no-op.

import { hapticHayahSuccess, hapticError } from "../../hayahHaptics";

const _jobs      = new Map(); // key -> { inFlight: bool, error: string|null }
const _listeners = new Map(); // key -> Set<cb>

// Status is also mirrored to localStorage under `${key}__status` so a
// component that mounts AFTER runJob started (e.g. user navigated away
// and back) sees the in-flight state even if it missed the initial
// notify. Module-level _jobs is the fast path; localStorage is the
// always-correct durable backstop.
const STALE_MS = 5 * 60 * 1000; // 5 min — past this we treat 'loading' as a crashed job
const JOB_TIMEOUT_MS = 60 * 1000; // 60s — cap any single AI call; beyond this it's effectively hung

function _statusKey(key) { return `${key}__status`; }

function _writeStatus(key, status) {
  try { localStorage.setItem(_statusKey(key), JSON.stringify(status)); } catch {}
}

function _readStatus(key) {
  try {
    const s = JSON.parse(localStorage.getItem(_statusKey(key)) || "null");
    if (!s) return null;
    // Reject loading-state entries older than STALE_MS — a job that hasn't
    // returned in 5 min has effectively died (network drop, app killed
    // mid-flight, etc.); don't pin the UI on it forever.
    if (s.phase === "loading" && s.startedAt && (Date.now() - s.startedAt) > STALE_MS) {
      return null;
    }
    return s;
  } catch { return null; }
}

function _notify(key) {
  const set = _listeners.get(key);
  if (set) set.forEach(cb => { try { cb(); } catch {} });
}

export function isInFlight(key) {
  if (_jobs.get(key)?.inFlight) return true;
  return _readStatus(key)?.phase === "loading";
}

export function lastError(key) {
  if (_jobs.has(key)) return _jobs.get(key).error || null;
  const s = _readStatus(key);
  return s?.phase === "error" ? (s.error || "Generation failed") : null;
}

export function clearError(key) {
  const cur = _jobs.get(key);
  if (cur) {
    _jobs.set(key, { ...cur, error: null });
  }
  const s = _readStatus(key);
  if (s?.phase === "error") {
    try { localStorage.removeItem(_statusKey(key)); } catch {}
  }
  _notify(key);
}

// Subscribe to in-flight / error transitions for a specific job key.
// Returns an unsubscribe function. Callbacks fire on every state
// transition AND once immediately at subscribe time — the latter
// removes the read-then-subscribe race where a job that completed
// between the caller's explicit isInFlight check and this subscribe
// would otherwise leave the UI pinned to its pre-completion state.
export function subscribe(key, cb) {
  if (!_listeners.has(key)) _listeners.set(key, new Set());
  _listeners.get(key).add(cb);
  try { cb(); } catch {}
  return () => {
    const set = _listeners.get(key);
    if (!set) return;
    set.delete(cb);
    if (set.size === 0) _listeners.delete(key);
  };
}

// Run an async generator under the given key. The generator is
// responsible for the actual Gemini call and for persisting its result
// (typically to localStorage). When the generator resolves we fire a
// success haptic; if it throws we capture the error and fire an error
// haptic. Either way, all subscribers are notified so any mounted UI
// can re-render against the fresh cache.
export async function runJob(key, generator) {
  if (isInFlight(key)) return;
  _jobs.set(key, { inFlight: true, error: null });
  _writeStatus(key, { phase: "loading", startedAt: Date.now() });
  _notify(key);
  let err = null;
  try {
    let timeoutId;
    const timeout = new Promise((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error("Timed out — tap to retry")),
        JOB_TIMEOUT_MS
      );
    });
    try {
      await Promise.race([generator(), timeout]);
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (e) {
    err = e?.message || "Generation failed";
  }
  _jobs.set(key, { inFlight: false, error: err });
  if (err) {
    _writeStatus(key, { phase: "error", error: err, finishedAt: Date.now() });
  } else {
    // On success the result is in localStorage[key]; clear the status
    // sentinel so subsequent reads return null = not loading, not errored.
    try { localStorage.removeItem(_statusKey(key)); } catch {}
  }
  _notify(key);
  try {
    if (err) hapticError();
    else     hapticHayahSuccess();
  } catch {}
}
